'use client';

import { forwardRef, useImperativeHandle } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  type ElementNode,
  type LexicalNode,
} from 'lexical';
import {
  $createBeautifulMentionNode,
  $isBeautifulMentionNode,
} from 'lexical-beautiful-mentions';
import type {
  MentionEntity,
  MessageSegment,
  ObjectContextMentionData,
} from '@terra/shared-dto-chat-types';
import type { MentionEntityWithOffset } from './buildMessageSegments';
import { getEditorCaretPosition } from '../../../mentions/get-editor-caret-rect';
import { resolveLexicalCaretAnchorDom } from '../resolve-lexical-caret-anchor';
import {
  normalizeSerializedEditorStateForHistory,
  resetEditorToEmptyParagraph,
} from './editorParagraphNormalization';
import { isMentionEntityType } from '../utils/mentionEntityType';

export type { MentionEntityWithOffset } from './buildMessageSegments';
export {
  buildMessageSegments,
  buildMentionDisplaySegments,
} from './buildMessageSegments';

export interface ImperativeEditorRef {
  getContent: () => string;
  getMentions: () => MentionEntityWithOffset[];
  setContent: (content: string) => void;
  appendText: (text: string) => void;
  clearContent: () => void;
  insertMention: (entity: MentionEntity) => void;
  setContentFromMessageSegments: (args: {
    segments: MessageSegment[];
    mentions: MentionEntity[];
  }) => void;
  focus: () => void;
  getMentionsAnchorPosition: () => { x: number; y: number } | undefined;
  getEditorState: () => string;
  getEditorStateForHistory: () => string;
  setEditorState: (state: string) => void;
  subscribeToChanges: (cb: (state: string) => void) => () => void;
}

const createMentionNodeFromEntity = (entity: MentionEntity) => {
  const mentionMenuItemId =
    typeof entity.extraData?.mentionMenuItemId === 'string'
      ? entity.extraData.mentionMenuItemId
      : null;
  const objectContext = entity.extraData?.objectContext;
  let objectKind: string | undefined;
  let objectContextJson: string | undefined;

  if (
    entity.entityType === 'ObjectContext' &&
    objectContext &&
    typeof objectContext === 'object'
  ) {
    const kind = (objectContext as ObjectContextMentionData).objectKind;

    if (typeof kind === 'string') {
      objectKind = kind;
    }

    objectContextJson = JSON.stringify(objectContext);
  }

  const extraDataJson = entity.extraData
    ? JSON.stringify(entity.extraData)
    : undefined;

  return $createBeautifulMentionNode('@', entity.label, {
    entityId: entity.entityId,
    entityType: entity.entityType,
    mentionMenuItemId,
    ...(objectKind ? { objectKind } : {}),
    ...(objectContextJson ? { objectContextJson } : {}),
    ...(extraDataJson ? { extraDataJson } : {}),
  });
};

export const ImperativeRefPlugin = forwardRef<ImperativeEditorRef>(
  (props, ref) => {
    const [editor] = useLexicalComposerContext();

    useImperativeHandle(ref, () => ({
      getContent: () => {
        let content = '';

        editor.getEditorState().read(() => {
          content = $getRoot().getTextContent();
        });

        return content;
      },
      getMentions: () => {
        const result: MentionEntityWithOffset[] = [];

        editor.getEditorState().read(() => {
          const root = $getRoot();
          let charOffset = 0;

          const traverse = (node: LexicalNode) => {
            if ($isBeautifulMentionNode(node)) {
              const nodeData = node.getData();
              const entityId = nodeData?.entityId;
              const entityType = nodeData?.entityType;

              if (
                typeof entityId === 'string' &&
                typeof entityType === 'string' &&
                isMentionEntityType(entityType)
              ) {
                const objectContextJson = nodeData?.objectContextJson;
                let extraData: MentionEntity['extraData'] | undefined;

                if (typeof objectContextJson === 'string') {
                  try {
                    const parsed: unknown = JSON.parse(objectContextJson);

                    if (parsed && typeof parsed === 'object') {
                      extraData = { objectContext: parsed };
                    }
                  } catch {
                    extraData = undefined;
                  }
                }

                result.push({
                  offset: charOffset,
                  entityId,
                  entityType,
                  label: node.getValue(),
                  ...(extraData ? { extraData } : {}),
                });
              }

              charOffset += node.getTextContent().length;
            } else if ($isTextNode(node)) {
              charOffset += node.getTextContent().length;
            } else if ($isElementNode(node)) {
              for (const child of node.getChildren()) {
                traverse(child);
              }

              if (node.getParent() !== null && node.getNextSibling() !== null) {
                charOffset += 1;
              }
            }
          };

          traverse(root);
        });

        return result;
      },
      setContent: (newContent: string) => {
        editor.update(() => {
          const root = $getRoot();

          root.clear();
          const paragraph = $createParagraphNode();

          paragraph.append($createTextNode(newContent));
          root.append(paragraph);
        });
      },
      appendText: (text: string) => {
        editor.update(() => {
          const root = $getRoot();
          const lastChild = root.getLastChild();
          const textNode = $createTextNode(text);
          const hasExistingContent = root.getTextContent().length > 0;

          if (!hasExistingContent && $isElementNode(lastChild)) {
            lastChild.append(textNode);
          } else {
            const paragraph = $createParagraphNode();

            paragraph.append(textNode);
            root.append(paragraph);
          }

          const end = textNode.getTextContentSize();

          textNode.select(end, end);
        });
      },
      clearContent: () => {
        editor.update(() => {
          resetEditorToEmptyParagraph();
        });
      },
      insertMention: (entity: MentionEntity) => {
        editor.update(() => {
          const mentionNode = createMentionNodeFromEntity(entity);
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            selection.insertNodes([mentionNode]);
          } else {
            const root = $getRoot();
            const lastChild = root.getLastChild();
            let targetNode: ElementNode;

            if ($isElementNode(lastChild)) {
              targetNode = lastChild;
            } else {
              const paragraph = $createParagraphNode();

              root.append(paragraph);
              targetNode = paragraph;
            }

            targetNode.append(mentionNode);
          }

          const spaceNode = $createTextNode(' ');

          mentionNode.insertAfter(spaceNode);
          spaceNode.select();
        });
      },
      setContentFromMessageSegments: ({
        segments,
        mentions,
      }: {
        segments: MessageSegment[];
        mentions: MentionEntity[];
      }) => {
        editor.update(() => {
          const root = $getRoot();

          root.clear();
          const paragraph = $createParagraphNode();

          for (const segment of segments) {
            if (segment.type === 'text') {
              if (segment.value.length > 0) {
                paragraph.append($createTextNode(segment.value));
              }

              continue;
            }

            const entity = mentions.find(
              (mention) => mention.entityId === segment.entityId,
            ) ?? {
              entityId: segment.entityId,
              entityType: segment.entityType,
              label: segment.label,
              extraData: {},
            };

            paragraph.append(createMentionNodeFromEntity(entity));
          }

          root.append(paragraph);
        });
      },
      focus: () => {
        editor.focus();
      },
      getEditorState: () => {
        return JSON.stringify(editor.getEditorState().toJSON());
      },
      getEditorStateForHistory: () => {
        const serialized = JSON.stringify(editor.getEditorState().toJSON());

        return normalizeSerializedEditorStateForHistory(serialized);
      },
      setEditorState: (serialized: string) => {
        try {
          const editorState = editor.parseEditorState(serialized);

          editor.setEditorState(editorState);
        } catch (error: unknown) {
          console.warn('[ImperativeRefPlugin] Failed to restore editor state', {
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      },
      subscribeToChanges: (cb: (state: string) => void) => {
        return editor.registerUpdateListener(() => {
          cb(JSON.stringify(editor.getEditorState().toJSON()));
        });
      },
      getMentionsAnchorPosition: () => {
        const rootElement = editor.getRootElement();

        if (!rootElement) {
          return undefined;
        }

        const lexicalAnchor = editor
          .getEditorState()
          .read(() => resolveLexicalCaretAnchorDom({ editor }));

        return getEditorCaretPosition({
          editorRoot: rootElement,
          fallbackAnchorElement: lexicalAnchor?.anchorElement,
          fallbackOffset: lexicalAnchor?.textOffset,
        });
      },
    }));

    return null;
  },
);
